import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import debounce from "lodash/debounce";
import {
  Button,
  VStack,
  Box,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Avatar,
  Image,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
} from "@chakra-ui/react";
import { FiCheck, FiChevronDown, FiPlus, FiSearch } from "react-icons/fi";
import { inspecto } from "modules/utils";

// Types remain the same
export interface FilterOption {
  value: string;
  label: string;
  image?: string | React.ComponentType;
  subtitle?: string;
  [key: string]: any;
}

interface FilterDropdownProps {
  initialOptions: FilterOption[];
  value: FilterOption[];
  onChange: (value: FilterOption[]) => void;
  placeholder?: string;
  searchable?: boolean;
  isError?: boolean;
  icon?: React.ComponentType;
  size?: "sm" | "xs";
  showCount?: number;
  onClose?: () => void;
  createable?: boolean;
  adaptableSelectWidth?: boolean;
  onCreateOption?: (label: string) => Promise<FilterOption>;
  defaultOpen?: boolean;
  singleSelect?: boolean;
  asyncSearch?: (term: string) => void;
  borders?: boolean;
  previewSelection?: boolean;
}

// Memoized Components
const ListItem = React.memo(
  ({
    option,
    isSelected,
    onClick,
    showFallbackImage,
  }: {
    option: FilterOption;
    isSelected: boolean;
    onClick: () => void;
    showFallbackImage: boolean;
  }) => (
    <Box
      px="3"
      py="2"
      cursor="pointer"
      onClick={onClick}
      _hover={{ bg: "gray.50" }}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <HStack spacing="2">
        {option.image ? (
          <Box boxSize="5" borderRadius="sm" overflow="hidden" flexShrink={0}>
            {typeof option.image === "string" ? (
              <Image
                rounded={6}
                src={option.image}
                alt={option.label}
                w="full"
                h="full"
                objectFit="cover"
              />
            ) : (
              <Icon as={option.image} boxSize="4" />
            )}
          </Box>
        ) : showFallbackImage ? (
          <Avatar name={option.label} size="xs" objectFit="cover" rounded={6} />
        ) : null}
        <VStack spacing={0} align="flex-start" w="100%">
          <Text fontSize="sm" fontWeight="medium" whiteSpace={"pre"}>
            {option.label}
          </Text>
          {option.subtitle && (
            <Text fontSize="xs" color="gray.500" isTruncated>
              {option.subtitle}
            </Text>
          )}
        </VStack>
      </HStack>
      {isSelected && (
        <Icon as={FiCheck} color="blue.500" boxSize="4" flexShrink={0} />
      )}
    </Box>
  ),
);

export const FilterDropdown: React.FC<FilterDropdownProps> = React.memo(
  ({
    initialOptions,
    value = [],
    onChange,
    placeholder = "Select...",
    searchable = true,
    defaultOpen = false,
    icon,
    showCount = 500,
    borders = true,
    isError = false,
    onClose = () => null,
    size = "sm",
    createable = false,
    onCreateOption,
    singleSelect = false,
    asyncSearch,
    adaptableSelectWidth = false,
    previewSelection = false,
  }) => {
    // Refs
    const buttonRef = useRef<HTMLButtonElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
      setTimeout(() => searchInputRef.current?.focus(), 200);
    }, [searchInputRef.current]);

    // State
    const [allOptions, setAllOptions] =
      useState<FilterOption[]>(initialOptions);
    const [searchQuery, setSearchQuery] = useState("");
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [isCreating, setIsCreating] = useState(false);
    const [buttonWidth, setButtonWidth] = useState<number>(0);

    // Memoized values and callbacks
    const selectedValuesSet = useMemo(
      () => new Set(value.map((v) => v.value)),
      [value],
    );

    const showFallbackImage = useMemo(
      () => allOptions.some((o) => Boolean(o.image)),
      [allOptions],
    );

    const filteredOptions = useMemo(() => {
      if (!searchQuery) return allOptions;
      const query = searchQuery.toLowerCase();
      return allOptions.filter(
        (opt) =>
          opt.label?.toLowerCase().includes(query) ||
          opt.subtitle?.toLowerCase().includes(query),
      );
    }, [allOptions, searchQuery]);

    const showCreateOption = useMemo(() => {
      if (!createable || !searchQuery) return false;
      return !filteredOptions.some(
        (opt) => opt.label.toLowerCase() === searchQuery.toLowerCase(),
      );
    }, [createable, searchQuery, filteredOptions]);

    // Virtualizer setup
    const rowVirtualizer = useVirtualizer({
      count: filteredOptions.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: useCallback(() => 55, []),
      overscan: 5,
    });

    // Callbacks
    const handleSearch = useCallback(
      debounce((term: string) => {
        if (asyncSearch) {
          asyncSearch(term);
        }
      }, 200),
      [asyncSearch],
    );

    const handleOptionSelect = useCallback(
      (option: FilterOption) => {
        if (singleSelect) {
          onChange([option]);
          setIsOpen(false);
        } else {
          const isSelected = selectedValuesSet.has(option.value);
          const newValue = isSelected
            ? value.filter((v) => v.value !== option.value)
            : [...value, option];
          onChange(newValue);
        }
      },
      [onChange, selectedValuesSet, singleSelect, value],
    );

    const handleCreateOption = useCallback(async () => {
      if (!searchQuery || isCreating) return;

      setIsCreating(true);
      try {
        const newOption = onCreateOption
          ? await onCreateOption(searchQuery)
          : { value: searchQuery, label: searchQuery };

        setAllOptions((prev) => [...prev, newOption]);
        onChange(singleSelect ? [newOption] : [...value, newOption]);
        setSearchQuery("");

        if (singleSelect) {
          setIsOpen(false);
        }
      } catch (error) {
        console.error("Failed to create option:", error);
      }
      setIsCreating(false);
    }, [
      searchQuery,
      isCreating,
      onCreateOption,
      singleSelect,
      value,
      onChange,
    ]);

    // Effects
    useEffect(() => {
      setAllOptions((prev) => {
        const createdOptions = prev.filter(
          (opt) =>
            !initialOptions.find((initial) => initial.value === opt.value),
        );
        return [...initialOptions, ...createdOptions];
      });
    }, [initialOptions]);

    useEffect(() => {
      const updateWidth = () => {
        if (buttonRef.current) {
          setButtonWidth(buttonRef.current.offsetWidth);
        }
      };

      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }, []);

    // Render helpers
    const getButtonText = useCallback(() => {
      if (value.length === 0) return placeholder;
      if (singleSelect) return value[0].label;
      if (previewSelection) return value.map((v) => v.label).join(", ");
      return `${value.length} selected`;
    }, [value, placeholder, singleSelect, previewSelection]);

    return (
      <Popover
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSearchQuery("");
          onClose();
        }}
        placement="bottom-start"
        isLazy
        closeOnBlur={true}
      >
        <PopoverTrigger>
          <Button
            ref={buttonRef}
            w="full"
            bg="white"
            variant="outline"
            display="flex"
            gap={2}
            size={size}
            fontWeight="normal"
            color="gray.700"
            border={borders ? "1px solid" : "none"}
            borderColor={isError ? "red.500" : "gray.300"}
            _hover={{ borderColor: "gray.400" }}
            onClick={() => setIsOpen(!isOpen)}
            sx={{
              "& > *:last-child": {
                marginLeft: icon ? "auto" : undefined,
              },
            }}
          >
            {icon && <Icon as={icon} boxSize="4" />}
            <Text isTruncated>{getButtonText()}</Text>
            <Icon as={FiChevronDown} boxSize="4" />
          </Button>
        </PopoverTrigger>

        <Portal>
          <Box zIndex={9999} position="relative" w="full">
            <PopoverContent
              minW="280px"
              maxW="100%"
              w={
                adaptableSelectWidth && buttonWidth
                  ? `${buttonWidth}px`
                  : "100%"
              }
              shadow="lg"
              borderColor="gray.200"
              _focus={{ outline: "none" }}
            >
              <PopoverBody p="0" w="full">
                <VStack align="stretch" spacing="0" w="full">
                  {searchable && (
                    <Box p="2" borderBottom="1px" borderColor="gray.100">
                      <InputGroup size="sm" autoFocus>
                        <InputLeftElement>
                          <Icon as={FiSearch} color="gray.400" boxSize="3" />
                        </InputLeftElement>
                        <Input
                          ref={searchInputRef}
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            handleSearch(e.target.value);
                          }}
                          autoFocus
                          placeholder="Search..."
                          _focus={{ borderColor: "gray.200", shadow: "none" }}
                          borderRadius="md"
                        />
                      </InputGroup>
                    </Box>
                  )}

                  <Box
                    ref={scrollRef}
                    maxH="450px"
                    overflowY="auto"
                    w="full"
                    position="relative"
                  >
                    <div
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const option = filteredOptions[virtualRow.index];
                        return (
                          <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                          >
                            <ListItem
                              option={option}
                              isSelected={selectedValuesSet.has(option.value)}
                              onClick={() => handleOptionSelect(option)}
                              showFallbackImage={showFallbackImage}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Box>

                  {showCreateOption && (
                    <Box
                      px="3"
                      py="2"
                      cursor={isCreating ? "wait" : "pointer"}
                      onClick={handleCreateOption}
                      _hover={{ bg: "gray.50" }}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      color="purple.500"
                    >
                      <HStack spacing="2">
                        <Icon as={FiPlus} boxSize="4" />
                        <Text fontSize="sm" fontWeight="medium">
                          Create "{searchQuery}"
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Box>
        </Portal>
      </Popover>
    );
  },
);

FilterDropdown.displayName = "FilterDropdown";

export default FilterDropdown;
